
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import type {
  AssignmentType, User, LogicType, PracticeAssigneesStatus,
} from '@/types';
import { currentUserHasPermission } from '@/helpers';
import MultiBoolSelect from './MultiBoolSelect.vue';

@Component({
  components: { MultiBoolSelect },
})
export default class AssignmentFilterToggle extends Vue {
  @Prop({ type: Object, required: true }) readonly filters!: Record<string, unknown>;

  @Prop({ type: Array, required: true }) readonly users!: User[];

  // Se false, la sincronizzazione iniziale di activeTypeIds viene rimandata:
  // il parent deve segnalare quando i filtri sono stati idratati dal DB
  // (es. dopo l'await di initXxxFilters + $set manuali nel created async).
  // Default true per retrocompatibilità con parent che idratano sincronamente.
  @Prop({ type: Boolean, default: true }) readonly filtersHydrated!: boolean;

  // Il parent può incrementare questo token per forzare un ri-sync di
  // activeTypeIds dai filtri, ad es. dopo il caricamento di un preset
  // che cambia programmaticamente più campi del filtro. Senza questo
  // meccanismo lo stato UI dei toggle tipo resterebbe invariato rispetto
  // ai filtri effettivi. Il primo mount NON richiede incremento
  // (la sync iniziale è gestita da filtersHydrated).
  @Prop({ type: Number, default: 0 }) readonly filtersSyncToken!: number;

  activeTypeIds: string[] = [];

  // True durante la sincronizzazione iniziale di activeTypeIds dai filtri caricati:
  // permette a onActiveTypesChange di saltare la logica di propagazione che altrimenti
  // sovrascriverebbe i filtri appena idratati dal DB.
  private syncingFromFilters = false;

  // Garantisce che la sincronizzazione iniziale avvenga una sola volta per montaggio.
  private activeTypesInitialized = false;

  get visibleTypes(): AssignmentType[] {
    const types = this.$store.getters.getAssignmentTypes as AssignmentType[];
    return types.filter((t) => this.hasPermission(t.permissionShow));
  }

  get effectiveTypeIds(): string[] {
    return this.activeTypeIds.length > 0
      ? this.activeTypeIds
      : this.visibleTypes.map((t) => t.id);
  }

  get assigneeUsers(): User[] {
    const groupIdSet = new Set<number>();
    this.effectiveTypeIds.forEach((typeId) => {
      const type = this.visibleTypes.find((t) => t.id === typeId);
      if (type) {
        (type.userGroupIds || []).forEach((gid: number) => groupIdSet.add(gid));
      }
    });
    if (groupIdSet.size === 0) return [];

    const groupIds = [...groupIdSet];
    const userItems = this.users.filter((u: User) => {
      const userGroups = u.groups.map((g) => g.id);
      return groupIds.some((gid: number) => userGroups.includes(gid));
    });

    userItems.unshift({ name: 'Non impostato', id: null } as unknown as User);

    return userItems;
  }

  get assignees(): (number | null)[] {
    const all = new Set<number | null>();
    this.effectiveTypeIds.forEach((typeId) => {
      const arr = this.filters[`${typeId}Assignees`];
      if (Array.isArray(arr)) {
        (arr as (number | null)[]).forEach((id) => all.add(id));
      }
    });
    return [...all];
  }

  set assignees(value: (number | null)[]) {
    this.effectiveTypeIds.forEach((typeId) => {
      this.$emit('update', typeId, {
        assignees: value,
        logic: this.logic,
        negated: this.negated,
      });
    });
  }

  get logic(): LogicType {
    const firstTypeId = this.effectiveTypeIds[0];
    if (!firstTypeId) return 'OR';
    return (this.filters[`${firstTypeId}AssigneesLogic`] as LogicType) || 'OR';
  }

  get negated(): (number | string)[] {
    const firstTypeId = this.effectiveTypeIds[0];
    if (!firstTypeId) return [];
    const val = this.filters[`${firstTypeId}AssigneesNegated`];
    return Array.isArray(val) ? val as (number | string)[] : [];
  }

  get status(): PracticeAssigneesStatus | null {
    // Quando activeTypeIds è vuoto (tutti i tipi sono "effective" di default),
    // leggere lo status dal primo tipo visibile lascerebbe il toggle spento
    // dopo il refresh se il filtro era stato impostato su un tipo diverso dal
    // primo (es. solo project con not-assigned mentre practice è null).
    // Scansioniamo quindi gli effective per trovare il primo tipo con status valorizzato.
    const firstWithStatus = this.effectiveTypeIds.find(
      (typeId) => !!this.filters[`${typeId}AssigneesStatus`],
    );
    if (firstWithStatus) {
      return this.filters[`${firstWithStatus}AssigneesStatus`] as PracticeAssigneesStatus;
    }
    const firstTypeId = this.effectiveTypeIds[0];
    if (!firstTypeId) return null;
    return (this.filters[`${firstTypeId}AssigneesStatus`] as PracticeAssigneesStatus) || null;
  }

  set status(value: PracticeAssigneesStatus | null) {
    this.effectiveTypeIds.forEach((typeId) => {
      this.$emit('statusChange', typeId, value);
    });
    this.visibleTypes.forEach((type) => {
      if (!this.effectiveTypeIds.includes(type.id)) {
        this.$emit('statusChange', type.id, null);
      }
    });
  }

  isTypeFilterActive(typeId: string): boolean {
    const assigneesKey = `${typeId}Assignees`;
    const statusKey = `${typeId}AssigneesStatus`;
    const assignees = this.filters[assigneesKey];
    const status = this.filters[statusKey];
    return (Array.isArray(assignees) && assignees.length > 0) || !!status;
  }

  onAssigneesUpdate(items: (number | null)[], logic: LogicType, negatedIds?: (number | string)[]) {
    this.effectiveTypeIds.forEach((typeId) => {
      this.$emit('update', typeId, {
        assignees: items,
        logic,
        negated: negatedIds || [],
      });
    });
    this.$emit('filtersChanged');
  }

  onStatusChange() {
    this.$emit('filtersChanged');
  }

  hasPermission(permission: string): boolean {
    return currentUserHasPermission(permission);
  }

  created() {
    if (this.$store.getters.getAssignmentTypes.length === 0) {
      this.$store.dispatch('fetchAssignmentTypes');
    }
  }

  @Watch('filters', { deep: true, immediate: true })
  onFiltersPropChange() {
    this.trySyncActiveTypesFromFilters();
  }

  @Watch('visibleTypes', { immediate: true })
  onVisibleTypesChange() {
    this.trySyncActiveTypesFromFilters();
  }

  @Watch('filtersHydrated', { immediate: true })
  onFiltersHydratedChange() {
    this.trySyncActiveTypesFromFilters();
  }

  @Watch('filtersSyncToken')
  onFiltersSyncTokenChange() {
    // Il parent ha modificato i filtri programmaticamente (es. caricamento preset):
    // resettiamo l'inizializzazione per ricalcolare activeTypeIds dai nuovi valori.
    this.activeTypesInitialized = false;
    this.trySyncActiveTypesFromFilters();
  }

  private trySyncActiveTypesFromFilters() {
    if (this.activeTypesInitialized) return;
    if (!this.visibleTypes || this.visibleTypes.length === 0) return;
    if (!this.filters) return;
    // Attendere l'idratazione dei filtri dal DB: se il parent è async
    // (Operations.vue await initOperationsFilters + $set), sincronizzare troppo
    // presto leggerebbe i default e marcherebbe activeTypesInitialized=true
    // a vuoto, saltando la sincronizzazione reale quando i valori arrivano.
    if (!this.filtersHydrated) return;

    const activeIds = this.visibleTypes
      .filter((t) => this.isTypeFilterActive(t.id))
      .map((t) => t.id);

    // Marca come inizializzato non appena filters+visibleTypes sono disponibili
    // E i filtri sono idratati, anche quando activeIds è vuoto: così evitiamo
    // che la sync scatti più tardi dopo interazioni utente, facendo scattare
    // l'early-return in onActiveTypesChange in un momento non previsto.
    this.activeTypesInitialized = true;

    const hasChanged = this.activeTypeIds.length !== activeIds.length
      || this.activeTypeIds.some((id, index) => id !== activeIds[index]);
    if (!hasChanged) return;

    this.syncingFromFilters = true;
    this.activeTypeIds = activeIds;
  }

  @Watch('activeTypeIds')
  onActiveTypesChange(newIds: string[], oldIds: string[]) {
    if (this.syncingFromFilters) {
      this.syncingFromFilters = false;
      return;
    }
    const oldEffective = oldIds.length > 0
      ? oldIds
      : this.visibleTypes.map((t) => t.id);
    const newEffective = newIds.length > 0
      ? newIds
      : this.visibleTypes.map((t) => t.id);

    // Read current filter state from the first old effective type
    const srcTypeId = oldEffective[0];
    let currentAssignees: (number | null)[] = [];
    let currentLogic: LogicType = 'OR';
    let currentNegated: (number | string)[] = [];
    let currentStatus: PracticeAssigneesStatus | null = null;

    if (srcTypeId) {
      const aKey = `${srcTypeId}Assignees`;
      currentAssignees = Array.isArray(this.filters[aKey])
        ? [...this.filters[aKey] as (number | null)[]]
        : [];
      currentLogic = (this.filters[`${srcTypeId}AssigneesLogic`] as LogicType) || 'OR';
      const nKey = `${srcTypeId}AssigneesNegated`;
      currentNegated = Array.isArray(this.filters[nKey])
        ? [...this.filters[nKey] as (number | string)[]]
        : [];
      currentStatus = (this.filters[`${srcTypeId}AssigneesStatus`] as PracticeAssigneesStatus) || null;
    }

    // Apply current state to all new effective types
    newEffective.forEach((typeId) => {
      this.$emit('update', typeId, {
        assignees: currentAssignees,
        logic: currentLogic,
        negated: currentNegated,
      });
      this.$emit('statusChange', typeId, currentStatus);
    });

    // Clear non-effective types
    this.visibleTypes.forEach((type) => {
      if (!newEffective.includes(type.id)) {
        this.$emit('update', type.id, { assignees: [], logic: 'OR', negated: [] });
        this.$emit('statusChange', type.id, null);
      }
    });

    this.$emit('filtersChanged');
  }
}
